import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import en from './locales/en.json';

export const LanguageContext = createContext();

// English ships with the app: it is the prerendered language and the fallback
// for any key a translation is missing. The other six are fetched on demand —
// bundling all of them put roughly 250 KB of copy nobody reads into the main
// bundle, on every visit.
const loaders = {
  en: () => Promise.resolve(en),
  ge: () => import(/* webpackChunkName: "locale-ge" */ './locales/ge.json').then((m) => m.default),
  ru: () => import(/* webpackChunkName: "locale-ru" */ './locales/ru.json').then((m) => m.default),
  es: () => import(/* webpackChunkName: "locale-es" */ './locales/es.json').then((m) => m.default),
  tr: () => import(/* webpackChunkName: "locale-tr" */ './locales/tr.json').then((m) => m.default),
  fr: () => import(/* webpackChunkName: "locale-fr" */ './locales/fr.json').then((m) => m.default),
  de: () => import(/* webpackChunkName: "locale-de" */ './locales/de.json').then((m) => m.default),
};

export const LANGUAGE_CODES = Object.keys(loaders);

const STORAGE_KEY = 'mlmind:lang';

// 'ge' is the app's own code for Georgian; the ISO code browsers and search
// engines expect is 'ka'.
const htmlLang = (code) => (code === 'ge' ? 'ka' : code);

// English is the default on every new visit. A language the visitor picks is
// kept for the rest of that browser session (reloads, new pages) but not
// beyond it, so the next visit opens in English again. Language URLs
// (/de, /fr, /es) still switch to their language — see LocaleUrlSync.
const readStoredLanguage = () => {
  try {
    // Earlier versions kept the choice permanently; drop it so returning
    // visitors start in English too.
    window.localStorage.removeItem(STORAGE_KEY);
    const saved = window.sessionStorage.getItem(STORAGE_KEY);
    if (saved && loaders[saved]) return saved;
  } catch (err) {
    // Private mode or blocked storage — fall through to the default.
  }
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  // Restore the choice made earlier in this session, so a reload does not
  // drop the visitor back to English mid-visit.
  const [selectedLanguage, setSelectedLanguage] = useState(readStoredLanguage);
  const [loaded, setLoaded] = useState({ en });
  const cache = useRef({ en });

  useEffect(() => {
    document.documentElement.lang = htmlLang(selectedLanguage);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, selectedLanguage);
    } catch (err) {
      // Nothing to do — the language still applies for this session.
    }
  }, [selectedLanguage]);

  // Fetch the chosen language once. Until it arrives the page stays in
  // English rather than blank, and a failed chunk simply leaves it there.
  useEffect(() => {
    let cancelled = false;
    if (cache.current[selectedLanguage]) return undefined;
    loaders[selectedLanguage]()
      .then((data) => {
        if (cancelled) return;
        cache.current[selectedLanguage] = data;
        setLoaded((current) => ({ ...current, [selectedLanguage]: data }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedLanguage]);

  const changeLanguage = useCallback((langCode) => {
    if (loaders[langCode]) setSelectedLanguage(langCode);
  }, []);

  // Merge over English so any untranslated key falls back to English instead of rendering blank.
  const activeTranslations = { ...en, ...(loaded[selectedLanguage] || {}) };

  // Tells the prerender (and anything else that cares) when the page is
  // actually showing the selected language rather than the English fallback.
  useEffect(() => {
    if (loaded[selectedLanguage]) document.documentElement.setAttribute('data-lang-ready', selectedLanguage);
  }, [loaded, selectedLanguage]);

  return (
    <LanguageContext.Provider value={{ selectedLanguage, changeLanguage, translations: activeTranslations }}>
      {children}
    </LanguageContext.Provider>
  );
};
